import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useStore } from "../../../stores/store";
import { observer } from "mobx-react-lite";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import FormLayout from '../../Generic/FormLayout';
import FormSelect from '../../Generic/FormSelect';
import CriterionValueGroup from '../../Generic/CriterionValueGroup';
import { useSetMenu } from "../../../hooks/useSetMenu";
import * as yup from "yup";
import './ToolFeature.css';
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import TuneIcon from "@mui/icons-material/Tune";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

const Edit = () => {
    const params = useParams();
    const navigate = useNavigate();
    const { mainStore } = useStore();
    const axiosPrivate = useAxiosPrivate();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    useSetMenu(mainStore.ToolFeatureMenuType, mainStore.ToolFeatureMenuNo);

    // Extract id from URL - useParams returns {*: 'ToolFeature/Edit/511'} due to catch-all route
    // So we need to parse the id from the pathname
    const id = React.useMemo(() => {
        // Try to get id from useParams first (standard way)
        if (params.id) return params.id;

        // If using catch-all route, extract from the * param or pathname
        const pathSegments = window.location.pathname.split('/');
        // Path is /ToolFeature/Edit/511, so id is the last segment
        const lastSegment = pathSegments[pathSegments.length - 1];
        return lastSegment && !isNaN(parseInt(lastSegment, 10)) ? lastSegment : null;
    }, [params]);

    // Yup schema for required cascading selects
    const schema = yup.object({
        panelId: yup.string().required(t("selectRequired")),
        categoryId: yup.string().required(t("selectRequired")),
        subcategoryId: yup.string().required(t("selectRequired")),
        toolPassportId: yup.string().required(t("selectRequired")),
    });

    const { register, handleSubmit, formState: { errors }, setValue } = useForm({ resolver: yupResolver(schema) });

    // Cascading select state
    const [panels, setPanels] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [toolPassports, setToolPassports] = useState([]);

    const [selectedPanel, setSelectedPanel] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubcategory, setSelectedSubcategory] = useState(null);
    const [selectedToolPassport, setSelectedToolPassport] = useState(null);

    // State for criteria grouped by weight coefficient
    const [criteriaW1, setCriteriaW1] = useState([]);
    const [criteriaW05, setCriteriaW05] = useState([]);
    const [criteriaW033, setCriteriaW033] = useState([]);
    // Separate selection states for each weight group
    const [selectedCVIdsW1, setSelectedCVIdsW1] = useState([]);
    const [selectedCVIdsW05, setSelectedCVIdsW05] = useState([]);
    const [selectedCVIdsW033, setSelectedCVIdsW033] = useState([]);

    // Loading flag for API operations
    const [loading, setLoading] = useState(false);
    const [notFound, setNotFound] = useState(false);

    // Memoized fetch function
    const fetchToolFeatureData = useCallback(async (featureId) => {
        try {
            setLoading(true);
            const response = await axiosPrivate.get(`/ToolFeatures/${featureId}`);
            const toolFeatureData = response.data;

            // Load panels
            const panelsResponse = await axiosPrivate.get("/Panels/SelectOptions");
            setPanels(panelsResponse.data);
            const selPanel = panelsResponse.data.find(c => c.value === toolFeatureData.panelId.toString());
            setSelectedPanel(selPanel);
            setValue("panelId", selPanel?.value, { shouldValidate: true });

            // Load categories
            if (selPanel) {
                const categoriesResponse = await axiosPrivate.get(`/Categories/SelectOptionsByPanel/${selPanel.value}`);
                setCategories(categoriesResponse.data);
                const selCategory = categoriesResponse.data.find(c => c.value === toolFeatureData.categoryId.toString());
                setSelectedCategory(selCategory);
                setValue("categoryId", selCategory?.value, { shouldValidate: true });

                // Load subcategories
                if (selCategory) {
                    const subcategoriesResponse = await axiosPrivate.get(`/Subcategories/SelectOptionsByCategory/${selCategory.value}`);
                    setSubcategories(subcategoriesResponse.data);
                    const selSubcategory = subcategoriesResponse.data.find(c => c.value === toolFeatureData.subcategoryId.toString());
                    setSelectedSubcategory(selSubcategory);
                    setValue("subcategoryId", selSubcategory?.value, { shouldValidate: true });

                    // Load tool passports
                    if (selSubcategory) {
                        const toolPassportsResponse = await axiosPrivate.get(`/ToolPassports/SelectOptionsBySubcategory/${selSubcategory.value}`);
                        setToolPassports(toolPassportsResponse.data);
                        const selectedTool = toolPassportsResponse.data.find(c => c.value === toolFeatureData.toolPassportId.toString());
                        setSelectedToolPassport(selectedTool);
                        setValue("toolPassportId", selectedTool?.value, { shouldValidate: true });

                        // Load criteria with values and group by weight
                        const criteriasResponse = await axiosPrivate.get(`/Criterias/WithValuesBySubcategory/${selSubcategory.value}`);
                        const data = criteriasResponse.data;
                        let allCriteria = [];
                        if (typeof data === "string" && data === "No records") {
                            allCriteria = [];
                        } else if (!Array.isArray(data)) {
                            allCriteria = [data];
                        } else {
                            allCriteria = data;
                        }

                        // Group criteria by weight coefficient
                        setCriteriaW1(allCriteria.filter((c) => c.weightCoefficient === 1.0));
                        setCriteriaW05(allCriteria.filter((c) => c.weightCoefficient === 0.5));
                        setCriteriaW033(allCriteria.filter((c) => c.weightCoefficient === 0.33));

                        // Set selected criteria value IDs per weight group from tool feature data
                        const selectedIds = toolFeatureData.criteriaValueIds || [];

                        // Build maps to know which criteria value belongs to which weight group
                        const w1ValueIds = new Set();
                        const w05ValueIds = new Set();
                        const w033ValueIds = new Set();

                        allCriteria.forEach(crit => {
                            crit.values.forEach(val => {
                                if (crit.weightCoefficient === 1.0) w1ValueIds.add(val.id);
                                else if (crit.weightCoefficient === 0.5) w05ValueIds.add(val.id);
                                else if (crit.weightCoefficient === 0.33) w033ValueIds.add(val.id);
                            });
                        });

                        setSelectedCVIdsW1(selectedIds.filter(id => w1ValueIds.has(id)));
                        setSelectedCVIdsW05(selectedIds.filter(id => w05ValueIds.has(id)));
                        setSelectedCVIdsW033(selectedIds.filter(id => w033ValueIds.has(id)));
                    }
                }
            }
        } catch (error) {
            if (error.response && error.response.status === 404) {
                setNotFound(true);
            } else {
                enqueueSnackbar(t("errorLoadingToolFeature"), { variant: "error" });
            }
        } finally {
            setLoading(false);
        }
    }, [axiosPrivate, setValue, enqueueSnackbar, t]);

    // Load tool feature data on mount
    useEffect(() => {
        if (id) {
            fetchToolFeatureData(id);
        }
    }, [id, fetchToolFeatureData]);

    // Handler: when panel changes, load categories
    const handlePanelChange = async (selectedOption) => {
        setValue("panelId", selectedOption ? selectedOption.value : "", { shouldValidate: true });
        setSelectedPanel(selectedOption);
        // Clear dependent selects
        setCategories([]);
        setSelectedCategory(null);
        setSubcategories([]);
        setSelectedSubcategory(null);
        setToolPassports([]);
        setSelectedToolPassport(null);
        // Clear criteria groups
        setCriteriaW1([]);
        setCriteriaW05([]);
        setCriteriaW033([]);
        setSelectedCVIdsW1([]);
        setSelectedCVIdsW05([]);
        setSelectedCVIdsW033([]);

        if (!selectedOption) return;
        try {
            const response = await axiosPrivate.get(`/Categories/SelectOptionsByPanel/${selectedOption.value}`);
            setCategories(
                typeof response.data === "string" && response.data === "No records" ? [] : response.data
            );
        } catch (error) {
            enqueueSnackbar(t("errorLoadingCategories"), { variant: "error" });
        }
    };

    // Handler: when category changes, load subcategories
    const handleCategoryChange = async (selectedOption) => {
        setValue("categoryId", selectedOption ? selectedOption.value : "", { shouldValidate: true });
        setSelectedCategory(selectedOption);
        setSubcategories([]);
        setSelectedSubcategory(null);
        setToolPassports([]);
        setSelectedToolPassport(null);
        // Clear criteria groups
        setCriteriaW1([]);
        setCriteriaW05([]);
        setCriteriaW033([]);
        setSelectedCVIdsW1([]);
        setSelectedCVIdsW05([]);
        setSelectedCVIdsW033([]);

        if (!selectedOption) return;
        try {
            const response = await axiosPrivate.get(`/Subcategories/SelectOptionsByCategory/${selectedOption.value}`);
            setSubcategories(
                typeof response.data === "string" && response.data === "No records" ? [] : response.data
            );
        } catch (error) {
            enqueueSnackbar(t("errorLoadingSubcategories"), { variant: "error" });
        }
    };

    // Handler: when subcategory changes, load tool passports and load criteria with values (for checkboxes)
    const handleSubcategoryChange = async (selectedOption) => {
        setValue("subcategoryId", selectedOption ? selectedOption.value : "", { shouldValidate: true });
        setSelectedSubcategory(selectedOption);
        setToolPassports([]);
        setSelectedToolPassport(null);
        // Clear criteria groups
        setCriteriaW1([]);
        setCriteriaW05([]);
        setCriteriaW033([]);
        setSelectedCVIdsW1([]);
        setSelectedCVIdsW05([]);
        setSelectedCVIdsW033([]);

        if (!selectedOption) return;
        try {
            // Load tool passports for this subcategory
            const response1 = await axiosPrivate.get(`/ToolPassports/SelectOptionsBySubcategory/${selectedOption.value}`);
            setToolPassports(
                typeof response1.data === "string" && response1.data === "No records" ? [] : response1.data
            );
        } catch (error) {
            enqueueSnackbar(t("errorLoadingToolPassports"), { variant: "error" });
        }
        try {
            // Load criteria with values for this subcategory and group by weight
            const response2 = await axiosPrivate.get(`/Criterias/WithValuesBySubcategory/${selectedOption.value}`);
            const data = response2.data;
            let allCriteria = [];
            if (typeof data === "string" && data === "No records") {
                allCriteria = [];
            } else if (!Array.isArray(data)) {
                allCriteria = [data];
            } else {
                allCriteria = data;
            }
            // Group by weight coefficient
            setCriteriaW1(allCriteria.filter((c) => c.weightCoefficient === 1.0));
            setCriteriaW05(allCriteria.filter((c) => c.weightCoefficient === 0.5));
            setCriteriaW033(allCriteria.filter((c) => c.weightCoefficient === 0.33));
        } catch (error) {
            enqueueSnackbar(t("errorLoadingCriteria"), { variant: "error" });
        }
    };

    // Handler: when tool passport changes
    const handleToolPassportChange = (selectedOption) => {
        setValue("toolPassportId", selectedOption ? selectedOption.value : "", { shouldValidate: true });
        setSelectedToolPassport(selectedOption);
    };

    // Submit handler for updating tool feature
    const onSubmit = async (data) => {
        // Combine all selected criteria value IDs from all weight groups
        const allSelectedIds = [...selectedCVIdsW1, ...selectedCVIdsW05, ...selectedCVIdsW033];

        if (allSelectedIds.length === 0) {
            enqueueSnackbar(t("pleaseSelectAtLeastOneCriteriaValue"), { variant: "warning" });
            return;
        }

        // Build the request with parsed integers and selected criteriaValueIds array
        const requestBody = {
            id: parseInt(id, 10),
            panelId: parseInt(data.panelId, 10),
            categoryId: parseInt(data.categoryId, 10),
            subcategoryId: parseInt(data.subcategoryId, 10),
            toolPassportId: parseInt(data.toolPassportId, 10),
            criteriaValueIds: allSelectedIds, // combined array of checked criteria values
        };

        try {
            setLoading(true);
            await axiosPrivate.put(`/ToolFeatures/${id}`, requestBody);
            enqueueSnackbar(t("updateSuccess"), { variant: "success" });
            navigate("/ToolFeature/List");
        } catch (error) {
            enqueueSnackbar(t("updateError"), { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    const onCancel = () => navigate(-1);

    if (notFound) {
        return (
            <div className="not-found-myDiv">
                <div className="not-found-container">
                    <div className="not-found-content">
                        <h1>{t("notFound")}</h1>
                        <p>{t("requestedItemNotFound")}</p>
                        <button className="btn btn-primary" onClick={() => navigate("/ToolFeature/List")}>
                            {t("backToList")}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <FormLayout title={t("toolFeatureEditTitle")} onSubmit={handleSubmit(onSubmit)} onCancel={onCancel}>
            {/* Cascading selects */}
            <FormSelect
                label="Panel"
                name="panelId"
                value={selectedPanel}
                register={register}
                errors={errors}
                colSize={3}
                options={panels}
                onChange={handlePanelChange}
                isClearable={true}
            />
            <FormSelect
                label="Category"
                name="categoryId"
                value={selectedCategory}
                register={register}
                errors={errors}
                colSize={3}
                options={categories}
                onChange={handleCategoryChange}
                isClearable={true}
            />
            <FormSelect
                label="Subcategory"
                name="subcategoryId"
                value={selectedSubcategory}
                register={register}
                errors={errors}
                colSize={3}
                options={subcategories}
                onChange={handleSubcategoryChange}
                isClearable={true}
            />
            <FormSelect
                label="Tool Passport"
                name="toolPassportId"
                value={selectedToolPassport}
                register={register}
                errors={errors}
                colSize={3}
                options={toolPassports}
                onChange={handleToolPassportChange}
                isClearable={true}
            />

            {/* Criteria grouped by weight coefficient */}
            {(criteriaW1.length > 0 || criteriaW05.length > 0 || criteriaW033.length > 0) && (
                <div style={{ width: '100%', marginTop: '1rem' }}>
                    {/* Mandatory Criteria (weight = 1.00) */}
                    <div style={{ marginBottom: "1.5rem" }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '12px 16px',
                            background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                            borderRadius: '8px 8px 0 0',
                            marginBottom: '12px'
                        }}>
                            <PriorityHighIcon sx={{ color: '#fff', fontSize: 22 }} />
                            <div>
                                <h4 style={{ margin: 0, color: '#fff', fontSize: '1em', fontWeight: '600' }}>
                                    {t("Mandatory Criteria")} (W = 1.00)
                                </h4>
                                <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.75em' }}>
                                    {t("Highest priority criteria")}
                                </span>
                            </div>
                            <span style={{
                                marginLeft: 'auto',
                                background: 'rgba(255,255,255,0.2)',
                                color: '#fff',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '0.75em',
                                fontWeight: '600'
                            }}>
                                {selectedCVIdsW1.length} / {criteriaW1.reduce((acc, c) => acc + c.values.length, 0)} {t("selected")}
                            </span>
                        </div>
                        {criteriaW1.length === 0 ? (
                            <div style={{
                                padding: '20px',
                                textAlign: 'center',
                                color: '#94a3b8',
                                background: '#f8fafc',
                                borderRadius: '0 0 8px 8px',
                                border: '1px dashed #e2e8f0'
                            }}>
                                {t("No mandatory criteria available")}
                            </div>
                        ) : (
                            criteriaW1.map((crit) => (
                                <CriterionValueGroup
                                    key={crit.id}
                                    criterion={crit}
                                    selectedValueIds={selectedCVIdsW1}
                                    setSelectedValueIds={setSelectedCVIdsW1}
                                />
                            ))
                        )}
                    </div>

                    {/* Criteria (weight = 0.50) */}
                    <div style={{ marginBottom: "1.5rem" }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '12px 16px',
                            background: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)',
                            borderRadius: '8px 8px 0 0',
                            marginBottom: '12px'
                        }}>
                            <TuneIcon sx={{ color: '#fff', fontSize: 22 }} />
                            <div>
                                <h4 style={{ margin: 0, color: '#fff', fontSize: '1em', fontWeight: '600' }}>
                                    {t("Criteria")} (W = 0.50)
                                </h4>
                                <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.75em' }}>
                                    {t("Medium priority criteria")}
                                </span>
                            </div>
                            <span style={{
                                marginLeft: 'auto',
                                background: 'rgba(255,255,255,0.2)',
                                color: '#fff',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '0.75em',
                                fontWeight: '600'
                            }}>
                                {selectedCVIdsW05.length} / {criteriaW05.reduce((acc, c) => acc + c.values.length, 0)} {t("selected")}
                            </span>
                        </div>
                        {criteriaW05.length === 0 ? (
                            <div style={{
                                padding: '20px',
                                textAlign: 'center',
                                color: '#94a3b8',
                                background: '#f8fafc',
                                borderRadius: '0 0 8px 8px',
                                border: '1px dashed #e2e8f0'
                            }}>
                                {t("No criteria with weight 0.50 available")}
                            </div>
                        ) : (
                            criteriaW05.map((crit) => (
                                <CriterionValueGroup
                                    key={crit.id}
                                    criterion={crit}
                                    selectedValueIds={selectedCVIdsW05}
                                    setSelectedValueIds={setSelectedCVIdsW05}
                                />
                            ))
                        )}
                    </div>

                    {/* Criteria (weight = 0.33) */}
                    <div style={{ marginBottom: "1.5rem" }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '12px 16px',
                            background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                            borderRadius: '8px 8px 0 0',
                            marginBottom: '12px'
                        }}>
                            <AddCircleOutlineIcon sx={{ color: '#fff', fontSize: 22 }} />
                            <div>
                                <h4 style={{ margin: 0, color: '#fff', fontSize: '1em', fontWeight: '600' }}>
                                    {t("Criteria")} (W = 0.33)
                                </h4>
                                <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.75em' }}>
                                    {t("Lower priority criteria")}
                                </span>
                            </div>
                            <span style={{
                                marginLeft: 'auto',
                                background: 'rgba(255,255,255,0.2)',
                                color: '#fff',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '0.75em',
                                fontWeight: '600'
                            }}>
                                {selectedCVIdsW033.length} / {criteriaW033.reduce((acc, c) => acc + c.values.length, 0)} {t("selected")}
                            </span>
                        </div>
                        {criteriaW033.length === 0 ? (
                            <div style={{
                                padding: '20px',
                                textAlign: 'center',
                                color: '#94a3b8',
                                background: '#f8fafc',
                                borderRadius: '0 0 8px 8px',
                                border: '1px dashed #e2e8f0'
                            }}>
                                {t("No criteria with weight 0.33 available")}
                            </div>
                        ) : (
                            criteriaW033.map((crit) => (
                                <CriterionValueGroup
                                    key={crit.id}
                                    criterion={crit}
                                    selectedValueIds={selectedCVIdsW033}
                                    setSelectedValueIds={setSelectedCVIdsW033}
                                />
                            ))
                        )}
                    </div>
                </div>
            )}
        </FormLayout>
    );
};

export default observer(Edit);
